import { ActionOrigin, AnalyticsService } from 'base/analytics';
import { getApiHost } from 'base/api_host';
import { isSafari } from 'base/clipboard';
import { csvToString, filenameify } from 'base/csv';
import { injector } from 'base/dependency-container';
import { exists, UnreachableError } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { Button } from 'base/ui/button';
import { Checkbox } from 'base/ui/checkbox';
import { Dialog, DialogHeroImage } from 'base/ui/dialog';
import {
  IconCode,
  IconCopy,
  IconCsv,
  IconExternalLink,
  IconFigma,
  IconPricingCsv,
  IconReact,
  IconTxt,
  IconWebflow,
} from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { Link } from 'base/ui/link';
import { menuDivider, menuItem } from 'base/ui/menu_model';
import { Textarea } from 'base/ui/textarea';
import { TickRow } from 'base/ui/tick_row';
import { ToastQueue } from 'base/ui/toast';
import { TextRegularLight, TextSmallLight, TextTiny } from 'base/ui/typography';
import { DialogButtons } from 'blocks/auth/accept_plan_offer';
import { BlocksService } from 'blocks/blocks_service';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { createPreferenceService } from 'blocks/services/preference';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { computedFn } from 'mobx-utils';
import React from 'react';
import { EditorMode, editorModeStore } from './editor_mode';
import ExportFigmaSitemapImage from './export-figma-sitemap.file.webp';
import ExportFigmaStyleguideImage from './export-figma-styleguide.file.webp';
import ExportFigmaWireframeImage from './export-figma-wireframe.file.webp';
import ExportWebflowStyleguideImage from './export-webflow-styleguide.file.webp';
import ExportWebflowWireframeImage from './export-webflow-wireframe.file.webp';
import styles from './export_and_share.css';
import {
  ExportReactDialog,
  hasDismissedReactExportInstructions,
} from './export_react_dialog';
import {
  downloadArchive,
  exportSiteSource,
  showMissingComponentsWarning,
} from './export_react_source';
import { Page, Project, Space } from './model';
import { unrefPresentSections } from './model_helpers';
import { WebflowSafariWarningDialog } from './safari-warning';
import { SelectionStore } from './selection/store';
import { ProjectSession } from './session/session';
import { SharingSettingsDialog } from './sharing_settings';
import { StyleGuideExportReactDialog } from './styleguide/export_dialog_react';
export { ExportReactDialog, hasDismissedReactExportInstructions };

export const exportFormat = observable({
  get value() {
    return injector.resolve(createPreferenceService).get('exportFormat');
  },
  get isSourceCodeExport() {
    return this.value === 'react' || this.value === 'html';
  },
  setValue: (value: 'react' | 'html' | 'webflow' | 'figma') => {
    injector.resolve(createPreferenceService).set('exportFormat', value);
  },
});

function NewOptionLozenge() {
  return (
    <div className={styles.newOptionLozenge}>
      <TextTiny color="green">new</TextTiny>
    </div>
  );
}

export const exportFormatMenuItems = computed(() => {
  return [
    menuItem({
      label: 'Figma',
      onClick: () => exportFormat.setValue('figma'),
      icon: IconFigma,
      checked: exportFormat.value === 'figma',
    }),
    menuItem({
      label: 'Webflow',
      onClick: () => exportFormat.setValue('webflow'),
      icon: IconWebflow,
      checked: exportFormat.value === 'webflow',
    }),
    menuItem({
      label: 'React',
      onClick: () => exportFormat.setValue('react'),
      icon: IconReact,
      checked: exportFormat.value === 'react',
    }),
    menuItem({
      label: 'HTML',
      onClick: () => exportFormat.setValue('html'),
      icon: IconCode,
      checked: exportFormat.value === 'html',
    }),
  ];
});

export function useExportDialogState(
  project: Project,
  analytics: AnalyticsService,
  session: ProjectSession,
  blocksService: BlocksService,
  library: LibraryCache,
  toastQueue: ToastQueue,
  selection: SelectionStore,
  flags: FeatureFlagsStore,
): [
  startExport: (name: ExportDialogName, origin: ActionOrigin) => void,
  exportDialog: React.ReactNode,
] {
  const [ExportDialog, setDialog] = React.useState<
    (typeof ALL_EXPORT_DIALOGS)[ExportDialogName] | undefined
  >();
  const startExport = React.useCallback(
    async (name: ExportDialogName, origin: ActionOrigin) => {
      if (session.showAuthWall) {
        session.showAuthWall();
        return;
      }
      if (
        ['webflow', 'react', 'html'].includes(name) &&
        editorModeStore.get() !== EditorMode.Sitemap &&
        !session.productRestrictions.webflowExportEnabled
      ) {
        session.showUpgradeWall?.('export');
        return;
      }
      if (isProjectEmpty(project)) {
        setDialog(() => EmptySiteDialog);
        return;
      }
      if (name === 'react' || name === 'html') {
        if (editorModeStore.get() === EditorMode.Styleguide) {
          setDialog(() => StyleGuideExportReactDialog);
          return;
        }

        analytics.track({
          event: 'blocks/download',
          format: name,
          subject: 'site',
          origin,
        });
        const { files, componentsMissing } = await exportSiteSource(
          name,
          selection,
          project,
          library,
        );

        if (componentsMissing) {
          const abort = await new Promise((resolve) =>
            showMissingComponentsWarning({
              onConfirm: () => resolve(false),
              onCancel: () => resolve(true),
            }),
          );
          if (abort) {
            return;
          }
        }

        downloadArchive(
          files,
          project.name?.toLowerCase().replaceAll(' ', '-') ?? 'untitled',
        );
      }
      // Deprecated, but kept for compatibility with old analytics
      analytics.track({
        event: 'blocks/click-export',
        destination: name,
        method: 'header',
        componentSlugs: [],
        subject: 'site',
        origin,
      });
      // If pass a function (which a react component is) to a setStateAction, it'll
      // try to call that function silly
      setDialog(() => ALL_EXPORT_DIALOGS[name]);
    },
    [session, project, analytics, selection, library],
  );
  return [
    startExport,
    ExportDialog && (
      <ExportDialog
        library={library}
        session={session}
        blocksService={blocksService}
        toastQueue={toastQueue}
        project={project}
        onClose={() => setDialog(undefined)}
        flags={flags}
        analytics={analytics}
      />
    ),
  ];
}

export function exportMenu(
  startExport: (name: ExportDialogName, origin: ActionOrigin) => void,
  origin: ActionOrigin,
) {
  return [
    menuItem({
      label: 'Export to Figma',
      onClick: () => startExport('figma', origin),
      icon: IconFigma,
    }),
    menuItem({
      label: 'Export to Webflow',
      onClick: () => startExport('webflow', origin),
      icon: IconWebflow,
    }),
    menuItem({
      label: 'Export to React',
      onClick: () => startExport('react', origin),
      icon: IconReact,
    }),
    menuItem({
      label: 'Export to HTML',
      onClick: () => startExport('html', origin),
      icon: IconCode,
    }),
    menuDivider(),
    menuItem({
      label: 'Export to TXT',
      onClick: () => startExport('txt', origin),
      icon: IconTxt,
    }),
    menuItem({
      label: 'Export to CSV',
      onClick: () => startExport('csv', origin),
      icon: IconCsv,
    }),
    menuItem({
      label: 'Export to Excel (with pricing)',
      onClick: () => startExport('pricingCsv', origin),
      icon: IconPricingCsv,
    }),
  ].filter(exists);
}

function isProjectEmpty(project: Project) {
  return (
    project.homePage.sections.length === 0 &&
    project.homePage.subPages.length === 0 &&
    project.spaces.length === 0
  );
}

const ExportFigmaDialog = observer(function ExportFigmaDialog({
  onClose,
  analytics,
}: {
  onClose(): void;
  analytics: AnalyticsService;
}) {
  let heroImageSrc: string;

  const mode = editorModeStore.get();
  switch (mode) {
    case EditorMode.Sitemap:
      heroImageSrc = ExportFigmaSitemapImage;
      break;
    case EditorMode.Styleguide:
      heroImageSrc = ExportFigmaStyleguideImage;
      break;
    case EditorMode.Wireframe:
    case EditorMode.Design:
      heroImageSrc = ExportFigmaWireframeImage;
      break;
    default:
      throw new UnreachableError(mode);
  }

  return (
    <Dialog title="Export to Figma" onClose={onClose}>
      <DialogHeroImage
        src={heroImageSrc}
        aspectRatio="432 / 274"
        alt="Screenshot of the Relume Figma plugin"
      />
      <TextRegularLight>
        Add your Sitemaps, Wireframes, and Style Guides in Figma with the Relume
        Figma Kit and Relume Figma Plugin.
      </TextRegularLight>
      <Rows spacing={8}>
        <TickRow>
          <div className={styles.figmaTextAndButtonContainer}>
            Open the Relume Figma Kit.
            <div className={styles.cloneStyleGuideButtonContainer}>
              <Button
                icon={IconFigma}
                label="Open in Figma"
                href="https://www.figma.com/community/file/1078092050664989246"
                target="_blank"
                onClick={() => {
                  analytics.track({
                    event: 'project:figma_open_figma_kit_button_click',
                    method: 'modal',
                  });
                }}
              />
            </div>
          </div>
        </TickRow>
        <TickRow>
          <div className={styles.figmaTextAndButtonContainer}>
            Install the Relume Figma Plugin.
            <div className={styles.cloneStyleGuideButtonContainer}>
              <Button
                icon={IconFigma}
                label="Install plugin"
                href="https://www.figma.com/community/plugin/1245615905217691936/Relume-Library"
                target="_blank"
                onClick={() => {
                  analytics.track({
                    event: 'project:figma_install_plugin_button_click',
                    method: 'modal',
                  });
                }}
              />
            </div>
          </div>
        </TickRow>
        <TickRow>Import your project into Figma.</TickRow>
      </Rows>
    </Dialog>
  );
});

function ExportWebflowDialog({
  onClose,
  analytics,
}: {
  onClose(): void;
  analytics: AnalyticsService;
}) {
  let heroImageSrc: string;

  const mode = editorModeStore.get();
  switch (mode) {
    case EditorMode.Styleguide:
      heroImageSrc = ExportWebflowStyleguideImage;
      break;
    case EditorMode.Wireframe:
    case EditorMode.Design:
    case EditorMode.Sitemap:
      heroImageSrc = ExportWebflowWireframeImage;
      break;
    default:
      throw new UnreachableError(mode);
  }

  if (isSafari()) {
    return <WebflowSafariWarningDialog onClose={onClose} />;
  }

  return (
    <Dialog title="Install Webflow App" onClose={onClose}>
      <DialogHeroImage
        src={heroImageSrc}
        aspectRatio="432 / 274"
        alt="Relume plugin with Webflow"
      />
      <TextRegularLight>
        Install the Relume Webflow App to import your pages and Style Guide into
        Webflow at once.
      </TextRegularLight>
      <Rows spacing={8}>
        <TickRow type="one">
          <div className={styles.webflowTextAndButtonContainer}>
            Clone the Relume Style Guide for Webflow.
            <div className={styles.cloneStyleGuideButtonContainer}>
              <Button
                onClick={() => {
                  analytics.track({
                    event: 'project:webflow_clone_style_guide_button_click',
                    method: 'modal',
                  });

                  return window.open(
                    'https://webflow.com/made-in-webflow/website/relume-library-styleguide',
                    '_blank',
                  );
                }}
                label="Clone Style Guide"
              />
            </div>
          </div>
        </TickRow>
        <TickRow type="two">
          <div className={styles.webflowTextAndButtonContainer}>
            Install the Relume Webflow App on your site.
            <div className={styles.installWebflowAppButtonContainer}>
              <Button
                label="Install Webflow App"
                onClick={() => {
                  analytics.track({
                    event: 'project:webflow_install_app_button_click',
                    method: 'modal',
                  });

                  return window.open(
                    'https://webflow.com/apps/detail/relume',
                    '_blank',
                  );
                }}
              />
            </div>
          </div>
        </TickRow>
        <TickRow type="three">Import your project into Webflow.</TickRow>
      </Rows>
    </Dialog>
  );
}

function PrimaryCopyButton({
  text,
  label,
  icon,
}: {
  text: string;
  label: string;
  icon: React.ComponentType;
}) {
  const [copied, setCopied] = React.useState(false);
  const onClickCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };
  return (
    <Button
      icon={icon}
      label={copied ? 'Copied!' : label}
      disabled={copied}
      onClick={onClickCopy}
      variant="primary"
      size="large"
    />
  );
}

function ExportShareLinkDialog({
  onClose,
  library,
  session,
  blocksService,
  toastQueue,
}: {
  onClose(): void;
  library: LibraryCache;
  session: ProjectSession;
  blocksService: BlocksService;
  toastQueue: ToastQueue;
}) {
  return (
    <SharingSettingsDialog
      library={library}
      session={session}
      blocksService={blocksService}
      toastQueue={toastQueue}
      onClose={onClose}
    />
  );
}

const ExportCsvDialog = observer(function _ExportCsvDialog({
  project,
  onClose,
  flags,
}: {
  project: Project;
  onClose(): void;
  flags: FeatureFlagsStore;
}) {
  const csvText = csvToString([
    ...spaceToCsv(
      project.spaces.length > 0 ? 'Primary sitemap' : undefined,
      [project.homePage],
      project,
    ),
    ...project.spaces.flatMap((space) =>
      spaceToCsv(space.name, space.subPages, project),
    ),
  ]);
  const href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvText);
  const filename = filenameify(project.name, 'sitemap') + '.csv';

  return (
    <Dialog
      title="Export complete"
      onClose={onClose}
      buttons={
        <Button
          variant="primary"
          label="Close"
          onClick={onClose}
          size="large"
        />
      }
    >
      <TextRegularLight>
        If the download hasn't started,{' '}
        <Link href={href} download={filename} clickOnMount>
          click here to try again
        </Link>
        .
      </TextRegularLight>
      <TextRegularLight>
        You can open this CSV in Excel, Google Sheets, Numbers or other
        spreadsheet programs.
      </TextRegularLight>
    </Dialog>
  );
});

const ExportPricingCsvDialog = observer(function _ExportPricingCsvDialog({
  session,
  onClose,
}: {
  session: ProjectSession;
  onClose(): void;
}) {
  const link = `${getApiHost()}blocks/project/export_pricing_xlsx?${new URLSearchParams(
    [['token', session.projectToken]],
  )}`;

  return (
    <Dialog
      title="Export complete"
      onClose={onClose}
      buttons={
        <Button
          variant="primary"
          label="Close"
          onClick={onClose}
          size="large"
        />
      }
    >
      <TextRegularLight>
        If the download hasn't started,{' '}
        <Link href={link} clickOnMount>
          click here to try again
        </Link>
        .
      </TextRegularLight>
      <TextRegularLight>
        You can open this file in Excel, Google Sheets, Numbers or other
        spreadsheet programs.
      </TextRegularLight>
    </Dialog>
  );
});

const spaceToCsv = computedFn(
  (
    name: string | undefined,
    subPages: readonly Page[],
    project: Project,
  ): readonly string[][] => {
    const rows = [];
    rows.push(['Page', 'Section', 'Section Description']);
    for (const subPage of subPages) {
      rows.push(...pageToCsv(subPage, project));
    }
    return rows;
  },
);

const pageToCsv = computedFn(
  (page: Page, project: Project): readonly string[][] => {
    const pageSections = unrefPresentSections(page.sections, project);
    if (page.pageType === 'path') {
      const rows = [];
      for (const subPage of page.subPages) {
        rows.push(...pageToCsv(subPage, project));
      }
      return rows;
    }
    const rows = pageSections.length
      ? pageSections.map((s, i) => [
          i === 0 ? page.name : '',
          s.name,
          s.description,
        ])
      : [[page.name]];
    for (const subPage of page.subPages) {
      rows.push(...pageToCsv(subPage, project));
    }
    return rows;
  },
);

const ExportTxtDialog = observer(function _ExportTxtDialog({
  project,
  onClose,
  flags,
}: {
  project: Project;
  onClose(): void;
  flags: FeatureFlagsStore;
}) {
  const projectText = [
    ...pageTextLines(project.homePage, project),
    ...project.spaces.flatMap((space) => spaceTextLines(space, project)),
  ].join('\n');

  return (
    <Dialog
      title="Export complete"
      onClose={onClose}
      buttons={
        <PrimaryCopyButton
          label="Copy text"
          icon={IconCopy}
          text={projectText}
        />
      }
    >
      <TextRegularLight>
        Here's your site as text. Copy this into a Notion, Google Docs or
        something else:
      </TextRegularLight>
      <TextRegularLight>
        <Textarea value={projectText} maxHeight={80} />
      </TextRegularLight>
    </Dialog>
  );
});

const pageTextLines = computedFn(
  (page: Page, project: Project): readonly string[] => {
    // Path pages are not included in text export, but non-path subpages are.
    if (page.pageType === 'path') {
      const lines = [];
      for (const subPage of page.subPages) {
        lines.push(...pageTextLines(subPage, project));
      }
      return lines;
    }

    // Anything that's not a path should be included in the text export.
    const subPages = page.subPages.filter((p) => p.pageType !== 'path');
    const lines = [
      `Page: **${page.name}**`,
      ...unrefPresentSections(page.sections, project).map((s) =>
        s.description.trim().length === 0
          ? ` - ${s.name}`
          : ` - ${s.name}: ${s.description}`,
      ),
    ];
    if (subPages.length > 0) {
      lines.push('');
      lines.push(`Sub-pages: ` + subPages.map((p) => p.name).join(', '));
      lines.push('');
    }
    lines.push('');
    for (const subPage of page.subPages) {
      lines.push(...pageTextLines(subPage, project));
    }
    return lines;
  },
);

const spaceTextLines = computedFn(
  (space: Space, project: Project): readonly string[] => {
    const lines = [
      `Sitemap: **${space.name}**`,
      `Pages: ` + space.subPages.map((p) => p.name).join(', '),
      '',
    ];
    for (const subPage of space.subPages) {
      lines.push(...pageTextLines(subPage, project));
    }
    return lines;
  },
);

function EmptySiteDialog({ onClose }: { onClose(): void }) {
  return (
    <Dialog
      title="Your site is empty"
      onClose={onClose}
      buttons={
        <Button variant="primary" label="OK" onClick={onClose} size="large" />
      }
    >
      <TextRegularLight>
        Add some sections & pages to your site before exporting it.
      </TextRegularLight>
    </Dialog>
  );
}

export function hasDismissedHTMLExportInstructions() {
  return localStorage.getItem('dismissedHTMLExportInstructions') === 'true';
}

function dismissedHTMLExportInstructions() {
  localStorage.setItem('dismissedHTMLExportInstructions', 'true');
}

const htmlDocUrl = 'https://react-docs.relume.io/getting-started/html';
export function ExportHtmlDialog({ onClose }: { onClose(): void }) {
  const [visible] = React.useState(!hasDismissedHTMLExportInstructions());
  const [checked, setChecked] = React.useState(false);
  if (!visible) {
    return null;
  }
  const onConfirm = () => {
    if (checked) {
      dismissedHTMLExportInstructions();
    }
    onClose();
  };
  return (
    <Dialog title="Getting Started with HTML" onClose={onClose}>
      <div className={styles.exportHelpDialogContainer}>
        <Rows spacing={32}>
          <Rows spacing={24}>
            <Rows spacing={8}>
              <TextRegularLight>
                Ensure Tailwind is configured in your project.
              </TextRegularLight>
              <TextRegularLight>
                The Tailwind setup will depend on your development environment.
                Visit our <Link href={htmlDocUrl}>HTML Docs</Link> for basic
                setup examples and more information.
              </TextRegularLight>
            </Rows>
            <div className={styles.exportHelpDialogBanner}>
              <TextRegularLight>
                Note
                <TextRegularLight tag="span" color="grey">
                  : These components are static and do not include JavaScript
                  functionality.
                </TextRegularLight>
              </TextRegularLight>
            </div>
          </Rows>
          <Rows spacing={24}>
            <Rows spacing={4}>
              <Checkbox
                label="Don't show this again"
                size="large"
                value={checked}
                onChange={setChecked}
              />
              {checked && (
                <TextSmallLight color="grey">
                  You can find these instructions at any time in our{' '}
                  <Link href={htmlDocUrl}>HTML Docs</Link>.
                </TextSmallLight>
              )}
            </Rows>
            <DialogButtons>
              <Button
                variant="secondary"
                label="HTML Docs"
                iconRight={IconExternalLink}
                target="_blank"
                href={htmlDocUrl}
                size="large"
              />
              <Button
                variant="primary"
                label="Got it"
                onClick={onConfirm}
                size="large"
              />
            </DialogButtons>
          </Rows>
        </Rows>
      </div>
    </Dialog>
  );
}

const ALL_EXPORT_DIALOGS = {
  figma: ExportFigmaDialog,
  react: ExportReactDialog,
  html: ExportHtmlDialog,
  webflow: ExportWebflowDialog,
  shareLink: ExportShareLinkDialog,
  txt: ExportTxtDialog,
  csv: ExportCsvDialog,
  pricingCsv: ExportPricingCsvDialog,
} as const;
export type ExportDialogName = keyof typeof ALL_EXPORT_DIALOGS;
